import React, { useEffect, useRef, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Biggertext,
  Dyslexia,
  HighlightLinks,
  LineHeight,
  TextSpacing,
} from "../../assets";
import dyslexia from "../../assets/Frame 2996-9/df.png";
import { useSettings } from "../../context/SettingsContext";
import {
  postFeatureSelectMessage,
  postFeatureUnselectMessage,
} from "../../utils/message.helper";
import {
  AccessibilityAdjustableCard,
  AccessibilityClickableCard,
} from "../AccessibilityCards";
import { AccessibilityTooltipButton } from "../Buttons";
import { Feature, Features } from "../../constants/features";
import { BaseOption } from "../../types/options";
import { AdvancedFunctionKey } from "../../types/widget";
import { publicAssetUrl } from "../../config";

interface ContentAdjustmentOption extends BaseOption {
  title: string;
  icon: string;
  functionKey: string;
  settingKey: string;
}

const STORAGE_KEYS = {
  SELECTED_ITEMS: "selectedItems",
  CURRENT_SLIDE: "currentSlide",
} as const;

const TIMEOUT_DELAY = 1000;

// Extract Grid Item Configuration
const GRID_ITEMS_CONFIG: ContentAdjustmentOption[] = [
  {
    id: Features.CONTENT_HIGHLIGHT_LINKS,
    label: "highlight_links",
    title: Features.CONTENT_HIGHLIGHT_LINKS,
    icon: publicAssetUrl("link.png"),
    functionKey: "highlightLinks",
    settingKey: Features.CONTENT_HIGHLIGHT_LINKS,
  },
  {
    id: Features.CONTENT_BIGGER_TEXT,
    label: "bigger_text",
    title: Features.CONTENT_BIGGER_TEXT,
    icon: publicAssetUrl("text.png"),
    functionKey: "biggerText",
    settingKey: Features.CONTENT_BIGGER_TEXT,
  },
  {
    id: Features.CONTENT_ALIGN_CENTER,
    label: "align_center",
    title: Features.CONTENT_ALIGN_CENTER,
    icon: publicAssetUrl("textalign.png"),
    functionKey: "textAlign",
    settingKey: Features.CONTENT_ALIGN_CENTER,
  },
  {
    id: Features.CONTENT_TEXT_SPACING,
    label: "text_spacing",
    title: Features.CONTENT_TEXT_SPACING,
    icon: publicAssetUrl("textspace.png"),
    functionKey: "textSpacing",
    settingKey: Features.CONTENT_TEXT_SPACING,
  },
  {
    id: Features.CONTENT_ALIGN_LEFT,
    label: "align_left",
    title: Features.CONTENT_ALIGN_LEFT,
    icon: publicAssetUrl("textalign.png"),
    functionKey: "textAlign",
    settingKey: Features.CONTENT_ALIGN_LEFT,
  },
  {
    id: Features.CONTENT_BIGGER_TEXT,
    label: "bigger_text",
    title: Features.CONTENT_BIGGER_TEXT,
    icon: publicAssetUrl("text.png"),
    functionKey: "biggerText",
    settingKey: Features.CONTENT_BIGGER_TEXT,
  },
  {
    id: Features.CONTENT_ALIGN_RIGHT,
    label: "align_right",
    title: Features.CONTENT_ALIGN_RIGHT,
    icon: publicAssetUrl("textalign.png"),
    functionKey: "textAlign",
    settingKey: Features.CONTENT_ALIGN_RIGHT,
  },
  {
    id: Features.CONTENT_LINE_HEIGHT,
    label: "line_height",
    title: Features.CONTENT_LINE_HEIGHT,
    icon: publicAssetUrl("lineheight.png"),
    functionKey: "lineHeight",
    settingKey: Features.CONTENT_LINE_HEIGHT,
  },
  {
    id: Features.CONTENT_DYSLEXIC_FRIENDLY,
    label: "dyslexic_friendly",
    title: Features.CONTENT_DYSLEXIC_FRIENDLY,
    icon: dyslexia,
    functionKey: "legibleFonts",
    settingKey: Features.CONTENT_DYSLEXIC_FRIENDLY,
  },
];

interface ContentAdjustmentWidgetProps {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const rowContainerClassName =
  "flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2";

const ContentAdjustmentWidget: React.FC<ContentAdjustmentWidgetProps> = ({
  setIsModalOpen,
}) => {
  const [selectedItems, setSelectedItems] = useState<Feature[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.SELECTED_ITEMS);
    return stored ? JSON.parse(stored) : [];
  });
  const [currentSlide, setCurrentSlide] = useState<{ [key: string]: number }>(
    () => {
      const stored = localStorage.getItem(STORAGE_KEYS.CURRENT_SLIDE);
      return stored ? JSON.parse(stored) : {};
    }
  );
  const {
    selectedCheckBox,
    widgetButtonColor,
    widgetDataSettings,
    oversizeWidget,
  } = useSettings();
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const postSelectionMessage = React.useCallback(
    (title: Feature, isSelected: boolean) => {
      if (isSelected) {
        postFeatureUnselectMessage(title);
      } else {
        postFeatureSelectMessage(title);
      }
    },
    [postFeatureSelectMessage, postFeatureUnselectMessage]
  );

  // Add Error Boundaries for localStorage
  const safeSetStorage = React.useCallback((key: string, value: any) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn(`Failed to save to localStorage: ${error}`);
    }
  }, []);

  const safeGetStorage = React.useCallback((key: string, defaultValue: any) => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.warn(`Failed to read from localStorage: ${error}`);
      return defaultValue;
    }
  }, []);

  // Extract Keyboard Navigation Logic
  const handleTabNavigation = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, currentTitle: Feature) => {
  const allButtons = Array.from(buttonRefs.current.entries()) as [string, HTMLButtonElement][];
      const currentIndex = allButtons.findIndex(
        ([title]) => title === currentTitle
      );

      if (!event.shiftKey) {
        const nextIndex = (currentIndex + 1) % allButtons.length;
        allButtons[nextIndex][1].focus();
      } else {
        const prevIndex =
          (currentIndex - 1 + allButtons.length) % allButtons.length;
        allButtons[prevIndex][1].focus();
      }
    },
    []
  );

  const slidesData: { [key: string]: { text: Feature; image: string; label: string }[] } = React.useMemo(
    () => ({
      [Features.CONTENT_BIGGER_TEXT]: [
        { text: Features.DEFAULT, image: publicAssetUrl("text.png"), label: "default" },
        {
          text: Features.CONTENT_BIGGER_TEXT_3XL,
          image: publicAssetUrl("text.png"),
          label: "3_xl_bigger_text",
        },
        {
          text: Features.CONTENT_BIGGER_TEXT_2XL,
          image: publicAssetUrl("text.png"),
          label: "2_xl_bigger_text",
        },
        { text: Features.CONTENT_BIGGER_TEXT_XL, image: publicAssetUrl("text.png"), label: "xl_bigger_text" },
        { text: Features.CONTENT_BIGGER_TEXT, image: publicAssetUrl("text.png"), label: "l_bigger_text" },
      ],
      [Features.CONTENT_TEXT_SPACING]: [
        { text: Features.DEFAULT, image: publicAssetUrl("textspace.png"), label: "default" },
        { text: Features.CONTENT_TEXT_SPACING_HEAVY, image: publicAssetUrl("textspace.png"), label: "heavy_space" },
        {
          text: Features.CONTENT_TEXT_SPACING_MODERATE,
          image: publicAssetUrl("textspace.png"),
          label: "moderate_space",
        },
        { text: Features.CONTENT_TEXT_SPACING_LIGHT, image: publicAssetUrl("textspace.png"), label: "light_space" },
      ],
      [Features.CONTENT_DYSLEXIC_FRIENDLY]: [
        { text: Features.DEFAULT, image: publicAssetUrl("textalign.png"), label: "default" },
        { text: Features.CONTENT_LEGIBLE_FONTS, image: dyslexia, label: "legible_fonts" },
        { text: Features.CONTENT_DYSLEXIC_FONT, image: dyslexia, label: "dyslexic_font" },
      ],
      [Features.CONTENT_LINE_HEIGHT]: [
        { text: Features.DEFAULT, image: publicAssetUrl("lineheight.png"), label: "default" },
        {
          text: Features.CONTENT_LINE_HEIGHT_2X,
          image: publicAssetUrl("lineheight.png"),
          label: "line_height_(2x)",
        },
        {
          text: Features.CONTENT_LINE_HEIGHT_1_75X,
          image: publicAssetUrl("lineheight.png"),
          label: "line_height_(1.75x)",
        },
        {
          text: Features.CONTENT_LINE_HEIGHT_1_5X,
          image: publicAssetUrl("lineheight.png"),
          label: "line_height_(1.5x)",
        },
      ],
      "Text Align": [
        { text: Features.CONTENT_ALIGN_LEFT, image: publicAssetUrl("textalign.png"), label: "align_left" },
        { text: Features.CONTENT_ALIGN_RIGHT, image: publicAssetUrl("textalign.png"), label: "align_right" },
        {
          text: Features.CONTENT_ALIGN_CENTER,
          image: publicAssetUrl("textalign.png"),
          label: "align_center",
        },
        {
          text: Features.CONTENT_ALIGN_JUSTIFY,
          image: publicAssetUrl("textalign.png"),
          label: "align_justify",
        },
      ],
    }),
    []
  );

  const isInitialMount = useRef(true);

  useEffect(() => {
    const handleReset = (event: MessageEvent) => {
      if (event.data?.action === "RESET") {
        setSelectedItems([]);
        setCurrentSlide({});
        safeSetStorage(STORAGE_KEYS.SELECTED_ITEMS, []);
        safeSetStorage(STORAGE_KEYS.CURRENT_SLIDE, {});
      }
    };
    window.addEventListener("message", handleReset);
    return () => window.removeEventListener("message", handleReset);
  }, [safeSetStorage]);

  useEffect(() => {
    const handleProfileSelection = () => {
      if (
        selectedCheckBox.selected === Features.PROFILE_LOW_VISION ||
        selectedCheckBox.selected === Features.PROFILE_COGNITIVE_LEARNING
      ) {
        if (!selectedItems.includes(Features.CONTENT_BIGGER_TEXT)) {
          const targetIndex =
            slidesData[Features.CONTENT_BIGGER_TEXT].findIndex(
              (item) => item.label === "l_bigger_text"
            ) || 4;
          setSelectedItems((prev) => [
            ...Array.from(new Set([...prev, Features.CONTENT_BIGGER_TEXT])),
          ]);
          setCurrentSlide((prev) => ({
            ...prev,
            [Features.CONTENT_BIGGER_TEXT]: targetIndex,
          }));
          postFeatureSelectMessage(Features.CONTENT_BIGGER_TEXT);
        }
      }

      if (
        selectedCheckBox.selected === Features.PROFILE_DYSLEXIA ||
        selectedCheckBox.selected === Features.PROFILE_COGNITIVE_LEARNING
      ) {
        if (!selectedItems.includes(Features.CONTENT_DYSLEXIC_FRIENDLY)) {
          const targetIndex =
            slidesData[Features.CONTENT_DYSLEXIC_FRIENDLY].findIndex(
              (item) => item.label === "dyslexic_font"
            ) || 2;
          setSelectedItems((prev) => [
            ...Array.from(new Set([...prev, Features.CONTENT_DYSLEXIC_FRIENDLY])),
          ]);
          setCurrentSlide((prev) => ({
            ...prev,
            [Features.CONTENT_DYSLEXIC_FRIENDLY]: targetIndex,
          }));
          postFeatureSelectMessage(Features.CONTENT_DYSLEXIC_FONT);
        }
      }

      if (selectedCheckBox.selected === Features.PROFILE_COGNITIVE_LEARNING) {
        if (!selectedItems.includes(Features.CONTENT_HIGHLIGHT_LINKS)) {
          setSelectedItems((prev) => [...prev, Features.CONTENT_HIGHLIGHT_LINKS]);
          postFeatureSelectMessage(Features.CONTENT_HIGHLIGHT_LINKS);
        }
      }
    };

    const handleProfileUnselection = () => {
      if (
        selectedCheckBox.unSelected === Features.PROFILE_COGNITIVE_LEARNING ||
        selectedCheckBox.unSelected === Features.PROFILE_LOW_VISION
      ) {
        if (selectedItems.includes(Features.CONTENT_BIGGER_TEXT)) {
          setSelectedItems((prev) =>
            prev.filter((item) => item !== Features.CONTENT_BIGGER_TEXT)
          );
          setCurrentSlide((prev) => ({
            ...prev,
            [Features.CONTENT_BIGGER_TEXT]: 0,
          }));
          postFeatureUnselectMessage(Features.CONTENT_BIGGER_TEXT);
        }
      }

      if (
        selectedCheckBox.unSelected === Features.PROFILE_DYSLEXIA ||
        selectedCheckBox.unSelected === Features.PROFILE_ADHD ||
        selectedCheckBox.unSelected === Features.PROFILE_COGNITIVE_LEARNING
      ) {
        if (selectedItems.includes(Features.CONTENT_DYSLEXIC_FRIENDLY)) {
          setSelectedItems((prev) =>
            prev.filter((item) => item !== Features.CONTENT_DYSLEXIC_FRIENDLY)
          );
          setCurrentSlide((prev) => {
            const updated = { ...prev };
            delete updated[Features.CONTENT_DYSLEXIC_FRIENDLY];
            return updated;
          });
          postFeatureUnselectMessage(Features.CONTENT_DYSLEXIC_FRIENDLY);
        }
      }
      if (selectedCheckBox.unSelected === Features.PROFILE_COGNITIVE_LEARNING) {
        if (selectedItems.includes(Features.CONTENT_HIGHLIGHT_LINKS)) {
          setSelectedItems((prev) =>
            prev.filter((item) => item !== Features.CONTENT_HIGHLIGHT_LINKS)
          );
          postFeatureUnselectMessage(Features.CONTENT_HIGHLIGHT_LINKS);
        }
      }
    };

    if (!isInitialMount.current) {
      handleProfileSelection();
      handleProfileUnselection();
    }
  }, [selectedCheckBox, slidesData]);

  useEffect(() => {
    if (isInitialMount.current) {
      if (selectedItems.includes(Features.CONTENT_BIGGER_TEXT)) {
        const savedIndex = currentSlide[Features.CONTENT_BIGGER_TEXT] || 0;
        const savedSlide = slidesData[Features.CONTENT_BIGGER_TEXT][savedIndex];
        if (savedSlide && savedSlide.text.toLowerCase() !== "default") {
          postFeatureSelectMessage(savedSlide.text as Feature);
        }
      }
      isInitialMount.current = false;
    }
  }, [selectedItems, currentSlide, slidesData]);

  const handleItemClick = React.useCallback(
    (title: Feature) => {
      let newSelectedItems = selectedItems.includes(title)
        ? selectedItems.filter((item) => item !== title)
        : [...selectedItems, title];
      if (([Features.CONTENT_ALIGN_LEFT, Features.CONTENT_ALIGN_RIGHT, Features.CONTENT_ALIGN_CENTER] as Feature[]).includes(title)) {
        newSelectedItems = newSelectedItems.filter(
          (item) =>
            !([Features.CONTENT_ALIGN_LEFT, Features.CONTENT_ALIGN_RIGHT, Features.CONTENT_ALIGN_CENTER] as Feature[]).includes(item) ||
            item === title
        );
      }

      postSelectionMessage(title, selectedItems.includes(title));
      setSelectedItems(newSelectedItems);
      safeSetStorage(STORAGE_KEYS.SELECTED_ITEMS, newSelectedItems);
    },
    [selectedItems, postSelectionMessage, safeSetStorage]
  );

  const handleSlideChange = React.useCallback(
    (title: Feature, direction: "up" | "down") => {
      const slides = slidesData[title] || [];
      if (slides.length === 0) return;

      const currentIndex = currentSlide[title] || 0;
      const newIndex =
        direction === "up"
          ? (currentIndex + 1) % slides.length
          : (currentIndex - 1 + slides.length) % slides.length;

      const newSlide = slides[newIndex];
      const isDefault = newSlide.label === "default";
      const newSelectedItems = isDefault
        ? selectedItems.filter((item) => item !== title)
        : [...selectedItems.filter((item) => item !== title), title];

      setCurrentSlide((prev) => ({ ...prev, [title]: newIndex }));
      setSelectedItems(newSelectedItems);

      safeSetStorage(STORAGE_KEYS.SELECTED_ITEMS, newSelectedItems);
      safeSetStorage(STORAGE_KEYS.CURRENT_SLIDE, {
        ...currentSlide,
        [title]: newIndex,
      });

      if (isDefault) {
        postFeatureUnselectMessage(title);
      } else {
        postFeatureSelectMessage(newSlide.text as Feature);
      }
    },
    [selectedItems, currentSlide, slidesData, safeSetStorage, postFeatureSelectMessage, postFeatureUnselectMessage]
  );

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    title: Feature,
    isSlider: boolean = false
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (isSlider) {
        handleSlideChange(title, "up");
      } else {
        handleItemClick(title);
      }
    } else if (event.key === "Tab") {
      // Intercept Tab navigation at group boundary: if we're at the last button in
      // this content-adjustment group and the user tabs forward, move focus to
      // the first focusable element in the next group (color-adjustment).
      const allButtons = Array.from(buttonRefs.current.entries());
      const currentIndex = allButtons.findIndex(([t]) => t === title);

      if (!event.shiftKey && currentIndex === allButtons.length - 1) {
        // try to focus first button in color adjustment group
        const nextGroupFirst = document.querySelector(
          '[data-group="color-adjustment"] button'
        ) as HTMLElement | null;
        if (nextGroupFirst) {
          event.preventDefault();
          nextGroupFirst.focus();
          return;
        }
      }

      handleTabNavigation(event, title);
    }
  };

  const setButtonRef = (title: string, element: HTMLButtonElement | null) => {
    if (element) {
      buttonRefs.current.set(title, element);
    } else {
      buttonRefs.current.delete(title);
    }
  };

  useEffect(() => {
    const handleFocus = (event: FocusEvent) => {};
    document.addEventListener("focusin", handleFocus);
    return () => document.removeEventListener("focusin", handleFocus);
  }, []);

  // Simplify Conditional Rendering
  const shouldRenderItem = React.useCallback(
    (functionKey: string) =>
      widgetDataSettings?.settings?.advanced?.functions?.[
        functionKey as AdvancedFunctionKey
      ],
    [widgetDataSettings]
  );

  return (
    <>
      <div data-group="content-adjustment"
        className="p-2 bg-white rounded"
        style={{
          minHeight: oversizeWidget ? "auto" : "fit-content",
          paddingBottom: oversizeWidget ? "1rem" : "0.5rem",
        }}
      >
        <div className="grid grid-cols-1 gap-2">
          {/* Row 1 */}
          <div className={rowContainerClassName}>
            {shouldRenderItem("highlightLinks") && (
              <AccessibilityClickableCard
                item={{ ...GRID_ITEMS_CONFIG[0], icon: <HighlightLinks /> }}
                isSelected={selectedItems.includes(
                  GRID_ITEMS_CONFIG[0].id as Feature
                )}
                onClick={() => handleItemClick(GRID_ITEMS_CONFIG[0].id as Feature)}
                onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                  handleKeyDown(e, GRID_ITEMS_CONFIG[0].id as Feature)
                }
                setButtonRef={setButtonRef}
                tooltip={
                  <AccessibilityTooltipButton
                    tooltipTitle="highlight_links"
                    tooltipDescription="highlight_background_behind_links_to_make_it_more_visible"
                    shortcutKey="Alt + L"
                    widgetButtonColor={widgetButtonColor}
                    tooltipAriaLabel="highlight_links_tooltip"
                  />
                }
              />
            )}

            {shouldRenderItem("legibleFonts") && (
              <AccessibilityAdjustableCard
                item={{ ...GRID_ITEMS_CONFIG[8], icon: <Dyslexia /> }}
                selectedItems={selectedItems}
                gridItems={GRID_ITEMS_CONFIG}
                currentSlide={currentSlide}
                slidesData={slidesData}
                setButtonRef={setButtonRef}
                handleKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                  handleKeyDown(e, GRID_ITEMS_CONFIG[8].id as Feature, true)
                }
                handleSlideChange={handleSlideChange}
                tooltip={
                  <AccessibilityTooltipButton
                    tooltipTitle="dyslexic_font"
                    tooltipDescription="changes_the_font_on_the_website_to_a_dyslexic_friendly_font"
                    shortcutKey="Alt + D"
                    widgetButtonColor={widgetButtonColor}
                    tooltipAriaLabel="dyslexic_friendly_tooltip"
                  />
                }
              />
            )}
          </div>

          {/* Row 2 */}
          <div className={rowContainerClassName}>
            {shouldRenderItem("textAlign") && (
              <AccessibilityClickableCard
                item={{ ...GRID_ITEMS_CONFIG[2], icon: <AlignCenter /> }}
                isSelected={selectedItems.includes(
                  GRID_ITEMS_CONFIG[2].id as Feature
                )}
                onClick={() => handleItemClick(GRID_ITEMS_CONFIG[2].id as Feature)}
                onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                  handleKeyDown(e, GRID_ITEMS_CONFIG[2].id as Feature)
                }
                setButtonRef={setButtonRef}
                tooltip={
                  <AccessibilityTooltipButton
                    tooltipTitle="align_center"
                    tooltipDescription="position_the_text_to_be_in_the_center"
                    shortcutKey="Alt + M"
                    widgetButtonColor={widgetButtonColor}
                    tooltipAriaLabel="align_center_tooltip"
                  />
                }
              />
            )}

            {shouldRenderItem("biggerText") && (
              <AccessibilityAdjustableCard
                item={{ ...GRID_ITEMS_CONFIG[1], icon: <Biggertext /> }}
                selectedItems={selectedItems}
                gridItems={GRID_ITEMS_CONFIG}
                currentSlide={currentSlide}
                slidesData={slidesData}
                setButtonRef={setButtonRef}
                handleKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                  handleKeyDown(e, GRID_ITEMS_CONFIG[1].id as Feature, true)
                }
                handleSlideChange={handleSlideChange}
                tooltip={
                  <AccessibilityTooltipButton
                    tooltipTitle="bigger_text"
                    tooltipAriaLabel="bigger_text_tooltip"
                    tooltipDescription="changes_the_size_of_the_text_on_the_website"
                    shortcutKey="Alt + B"
                    widgetButtonColor={widgetButtonColor}
                  />
                }
              />
            )}
          </div>

          {/* Row 3 */}
          <div className={rowContainerClassName}>
            {shouldRenderItem("textAlign") && (
              <AccessibilityClickableCard
                item={{ ...GRID_ITEMS_CONFIG[4], icon: <AlignLeft /> }}
                isSelected={selectedItems.includes(
                  GRID_ITEMS_CONFIG[4].id as Feature
                )}
                onClick={() => handleItemClick(GRID_ITEMS_CONFIG[4].id as Feature)}
                onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                  handleKeyDown(e, GRID_ITEMS_CONFIG[4].id as Feature)
                }
                setButtonRef={setButtonRef}
                tooltip={
                  <AccessibilityTooltipButton
                    tooltipTitle="align_left"
                    tooltipDescription="text_is_aligned_to_the_left"
                    shortcutKey="Alt + Q"
                    widgetButtonColor={widgetButtonColor}
                    tooltipAriaLabel="align_left_tooltip"
                  />
                }
              />
            )}

            {shouldRenderItem("textSpacing") && (
              <AccessibilityAdjustableCard
                item={{ ...GRID_ITEMS_CONFIG[3], icon: <TextSpacing /> }}
                selectedItems={selectedItems}
                gridItems={GRID_ITEMS_CONFIG}
                currentSlide={currentSlide}
                slidesData={slidesData}
                setButtonRef={setButtonRef}
                handleKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                  handleKeyDown(e, GRID_ITEMS_CONFIG[3].id as Feature, true)
                }
                handleSlideChange={handleSlideChange}
                tooltip={
                  <AccessibilityTooltipButton
                    tooltipLabel="text_spacing_tooltip"
                    tooltipTitle="text_spacing"
                    tooltipDescription="changes_the_space_between_words"
                    shortcutKey="Alt + E"
                    widgetButtonColor={widgetButtonColor}
                  />
                }
              />
            )}
          </div>

          {/* Row 4 */}
          <div className={rowContainerClassName}>
            {shouldRenderItem("textAlign") && (
              <AccessibilityClickableCard
                item={{ ...GRID_ITEMS_CONFIG[6], icon: <AlignRight /> }}
                isSelected={selectedItems.includes(
                  GRID_ITEMS_CONFIG[6].id as Feature
                )}
                onClick={() => handleItemClick(GRID_ITEMS_CONFIG[6].id as Feature)}
                onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                  handleKeyDown(e, GRID_ITEMS_CONFIG[6].id as Feature)
                }
                setButtonRef={setButtonRef}
                tooltip={
                  <AccessibilityTooltipButton
                    tooltipTitle="align_right"
                    tooltipDescription="text_is_aligned_to_the_right"
                    shortcutKey="Alt + O"
                    tooltipAriaLabel="align_right_tooltip"
                    widgetButtonColor={widgetButtonColor}
                  />
                }
              />
            )}

            {shouldRenderItem("lineHeight") && (
              <AccessibilityAdjustableCard
                item={{ ...GRID_ITEMS_CONFIG[7], icon: <LineHeight /> }}
                selectedItems={selectedItems}
                gridItems={GRID_ITEMS_CONFIG}
                currentSlide={currentSlide}
                slidesData={slidesData}
                setButtonRef={setButtonRef}
                handleKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                  handleKeyDown(e, GRID_ITEMS_CONFIG[7].id as Feature, true)
                }
                handleSlideChange={handleSlideChange}
                tooltip={
                  <AccessibilityTooltipButton
                    tooltipLabel="line_height_tooltip"
                    tooltipTitle="line_height"
                    tooltipDescription="changes_the_line_height_of_texts"
                    shortcutKey="Alt + J"
                    widgetButtonColor={widgetButtonColor}
                  />
                }
              />
            )}
          </div>
        </div>
      </div>
      <div className="h-5" />
    </>
  );
};

export default ContentAdjustmentWidget;
